import { CommonModule, DatePipe } from '@angular/common';
import { Component, inject, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import dayjs from 'dayjs';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { ButtonModule } from "primeng/button";
import { ConfirmDialog } from 'primeng/confirmdialog';
import { ContextMenu, ContextMenuModule } from 'primeng/contextmenu';
import { TableModule } from "primeng/table";
import { FallaufgabenService } from '../../services/fallaufgaben-service';
import { Status } from '../../services/status';
import { SessionDetailsComponent } from "./session-details/session-details.component";
import { SessionDetailsService } from './session-details/session-details.service';

@Component({
  selector: 'app-sessions',
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    DatePipe,
    ConfirmDialog,
    ContextMenuModule,
    SessionDetailsComponent
  ],
  templateUrl: './sessions.html',
  styleUrl: './sessions.scss',
  providers: [ConfirmationService]
})
export class Sessions {
  @ViewChild('cm') cm!: ContextMenu;

  private messageService = inject(MessageService);
  private confirmService = inject(ConfirmationService);
  protected fallaufgabenService = inject(FallaufgabenService);
  protected statusService = inject(Status);
  public sessionDetailsService = inject(SessionDetailsService);
  private router = inject(Router);


  protected getKiResult() {
    this.statusService.getKiResultsForStatus();
    alert("es dauert ein wenig bis die ergebnisse aktualisiert wurden");
  }


  protected async getOpenAsUserLink() {
    const link = await this.statusService.getUserFallausgabenStatusLink();
    window.open(link);
  }

  protected async getOpenAsKorrektor() {
    const link = await this.statusService.getKorrekturFallausgabenStatusLink('corrector', 'regular');
    window.open(link);
  }

  protected getOpenAsKorrektorLabel(): string {
    return this.fallaufgabenService.selectedFallaufgabe?.type === 'vc'
      ? 'Zur Stichprobe öffnen'
      : 'Zur Korrektur öffnen';
  }

  protected async getOpenForNachkorrektur() {
    const link = await this.statusService.getKorrekturFallausgabenStatusLink('corrector', 'postcorrection');
    window.open(link);
  }

  protected async getOpenForTNB() {
    const link = await this.statusService.getKorrekturFallausgabenStatusLink('tnb', '');
    window.open(link);
  }

  protected async getOpenForMusterloesung() {
    const link = await this.statusService.getKorrekturFallausgabenStatusLink('participant', '');
    window.open(link);
  }

  protected openSessionDetails() {
    this.sessionDetailsService.openSessionDetails(this.statusService.selectedStatus);
  }

  protected backToFallaufgabe() {
    void this.router.navigate(["/fallaufgaben", this.fallaufgabenService.selectedFallaufgabe._id]);
  }

  protected isSessionRunning(endDate: string): boolean {
    return dayjs().isBefore(dayjs(endDate));
  }

  protected isSessionCorrectable(): boolean {
    return this.isSessionRunning(this.statusService.selectedStatus.endDate) && dayjs(this.statusService.selectedStatus.korrektorFinishedDate).toDate().getTime() == new Date(0).getTime();
  }

  protected async confirmDelete() {
    if (this.statusService.selectedStatus.isAdminTest) {
      this.confirmService.confirm({
        position: 'top',
        message: 'Sind Sie sicher, dass Sie diese Session löschen möchten?',
        icon: 'pi pi-exclamation-triangle',
        rejectButtonProps: {
          label: 'Abbrechen',
          severity: 'secondary',
          outlined: true
        },
        acceptButtonProps: {
          label: 'Löschen',
          severity: 'danger',
        },
        accept: async () => {
          this.statusService.delete();
          this.messageService.add({ severity: 'success', summary: 'Erfolgreich', detail: `Die Session wurde gelöscht.`, life: 3000 });
        }
      });
    }
  }

  protected onContextMenu(event: any, id: string) {
    this.statusService.set(id);
    this.cm.target = event.currentTarget;
    this.cm.model = this.getContextItems();
    this.cm.show(event);
  }

  protected getContextItems(): MenuItem[] {
    return [
      {
        label: 'Sitzungsdetails',
        icon: 'pi pi-search',
        command: () => { this.openSessionDetails(); }
      },
      {
        label: 'Aktuelle Sitzung öffnen',
        icon: 'pi pi-external-link',
        command: () => { this.getOpenAsUserLink(); }
      },
      {
        label: this.getOpenAsKorrektorLabel(),
        icon: 'pi pi-clipboard',
        command: () => { this.getOpenAsKorrektor(); },
        visible: this.statusService.selectedStatus.isAdminTest,
        disabled: this.fallaufgabenService.selectedFallaufgabe?.type !== 'vc' && this.isSessionCorrectable()
      },
      {
        label: 'Zur Nachkorrektur öffnen',
        icon: 'pi pi-clipboard',
        command: () => { this.getOpenForNachkorrektur(); },
        visible: this.statusService.selectedStatus.isAdminTest,
        disabled: this.fallaufgabenService.selectedFallaufgabe?.type !== 'vc' && this.isSessionCorrectable()
      },
      {
        label: 'Zur Einsicht öffnen (TNB)',
        icon: 'pi pi-clipboard',
        command: () => { this.getOpenForTNB(); },
        visible: this.statusService.selectedStatus.isAdminTest,
        disabled: false
      },
      {
        label: 'Musterlösung & Korrektur öffnen (Prüfling)',
        icon: 'pi pi-clipboard',
        command: () => { this.getOpenForMusterloesung(); },
        visible: this.statusService.selectedStatus.isAdminTest,
        disabled: false
      },
      {
        label: 'Ki Ergebnis',
        icon: 'pi pi-cog',
        command: () => { this.getKiResult(); },
        disabled: this.fallaufgabenService.selectedFallaufgabe?.type !== 'vc' && this.isSessionCorrectable(),
        visible: this.statusService.selectedStatus.isAdminTest
      },
      {
        label: 'Löschen',
        icon: 'pi pi-trash',
        command: () => { this.confirmDelete(); },
        visible: this.statusService.selectedStatus.isAdminTest
      }
    ];
  }
}